// Global Instructions Rule Applied!

import React, { useEffect, useMemo, useState } from 'react'
import { Button, Space, Typography, Spin, Alert, Empty } from 'antd'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { useAccountTopics } from '../hooks/useAccountTopics.ts'
import { TopicMatrix } from './TopicMatrix.tsx'
import { AccountTopicsModel } from '../../../state_manager/data_models/account_topics_grid_model.ts'

const { Text } = Typography

interface AccountTopicsTabProps {
  recordId?: string | null
  accountName?: string | null
}

type SelMap = Record<string, Set<string>>

// Stable snapshot of the current selection, used for dirty detection.
const snapshot = (acct: Set<string>, contacts: SelMap): string => {
  const a = Array.from(acct).sort().join(',')
  const c = Object.keys(contacts)
    .sort()
    .map((k) => `${k}:${Array.from(contacts[k]).sort().join(',')}`)
    .join('|')
  return `${a}#${c}`
}

const buildInitial = (data: AccountTopicsModel | null): { acct: Set<string>; contacts: SelMap } => {
  if (!data) return { acct: new Set(), contacts: {} }
  const acct = new Set(data.account_topics__comms_topic_ids ?? [])
  const contacts: SelMap = {}
  for (const c of data.contacts ?? []) {
    contacts[c.contacts__id] = new Set(c.contact_account_topics__comms_topic_ids ?? [])
  }
  return { acct, contacts }
}

export function AccountTopicsTab({ recordId, accountName }: AccountTopicsTabProps): React.ReactElement {
  const { data, loading, saving, error, refetch, save } = useAccountTopics(recordId)

  const [acctSel, setAcctSel] = useState<Set<string>>(new Set())
  const [contactSel, setContactSel] = useState<SelMap>({})
  const [baseline, setBaseline] = useState<string>('#')

  // (Re)seed local edit state whenever authoritative data arrives (mount,
  // refetch, post-save). This clears the dirty flag.
  useEffect(() => {
    const init = buildInitial(data)
    setAcctSel(init.acct)
    setContactSel(init.contacts)
    setBaseline(snapshot(init.acct, init.contacts))
  }, [data])

  const dirty = useMemo(
    () => snapshot(acctSel, contactSel) !== baseline,
    [acctSel, contactSel, baseline]
  )

  // Toggling an account topic cascades to its eligible contacts, mirroring
  // the Phase 0 propagate trigger + the contact ⊆ account invariant
  // (plan §1): turning a topic ON subscribes every eligible contact (so the
  // save's per-contact reconcile keeps the trigger-created rows instead of
  // soft-deleting them); turning it OFF removes it from every contact.
  // Individual contacts can then be un-ticked as explicit exclusions.
  const toggleAccount = (topicId: string) => {
    const turningOn = !acctSel.has(topicId)
    setAcctSel((prev) => {
      const next = new Set(prev)
      if (turningOn) next.add(topicId)
      else next.delete(topicId)
      return next
    })
    setContactSel((prev) => {
      const map: SelMap = { ...prev }
      for (const c of data?.contacts ?? []) {
        const s = new Set(map[c.contacts__id] ?? [])
        if (turningOn) s.add(topicId)
        else s.delete(topicId)
        map[c.contacts__id] = s
      }
      return map
    })
  }

  const toggleContact = (contactId: string, topicId: string) =>
    setContactSel((prev) => {
      const nextSet = new Set(prev[contactId] ?? [])
      if (nextSet.has(topicId)) nextSet.delete(topicId)
      else nextSet.add(topicId)
      return { ...prev, [contactId]: nextSet }
    })

  const topics = data?.comms_topics ?? []
  const contacts = data?.contacts ?? []

  const handleSave = async () => {
    await save({
      account_topic_ids: Array.from(acctSel),
      contacts: contacts.map((c) => ({
        contact_id: c.contacts__id,
        comms_topic_ids: Array.from(contactSel[c.contacts__id] ?? [])
      }))
    })
  }

  const handleDiscard = () => {
    const init = buildInitial(data)
    setAcctSel(init.acct)
    setContactSel(init.contacts)
  }

  if (loading && !data) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <Spin size='large' />
      </div>
    )
  }

  return (
    <div className='account-topics flex flex-col h-full p-3 overflow-hidden'>
      <FormSection
        title='Topic Subscriptions'
        titleColor='blue'
        scrollable={false}
        className='flex-1 min-h-0 flex flex-col'
        headerActions={
          <Space>
            <Button size='small' onClick={handleDiscard} disabled={!dirty || saving}>
              Discard
            </Button>
            <Button size='small' type='primary' onClick={handleSave} loading={saving} disabled={!dirty}>
              Save
            </Button>
          </Space>
        }
      >
        {error ? (
          <Alert
            type='error'
            showIcon
            className='mb-3'
            message='Could not load or save topic subscriptions'
            description={error}
            action={
              <Button size='small' onClick={refetch}>
                Retry
              </Button>
            }
          />
        ) : null}

        {topics.length === 0 ? (
          <Empty
            description='No topics defined yet. Create topics in Topic Management.'
            className='my-10'
          />
        ) : (
          <div className='flex-1 min-h-0 flex flex-col'>
            <TopicMatrix
              topics={topics}
              accountRowLabel={data?.accounts__name || accountName || 'This account'}
              contacts={contacts}
              accountSel={acctSel}
              contactSel={contactSel}
              disabled={saving}
              onToggleAccount={toggleAccount}
              onToggleContact={toggleContact}
            />
            {contacts.length === 0 ? (
              <Text type='secondary' className='mt-2'>
                No comms-eligible contacts for this account (Employee or Advisor
                relationships, current and active).
              </Text>
            ) : null}
          </div>
        )}
      </FormSection>

      <style>{`
        .account-topics .topic-matrix-account-row > td {
          background: #fafafa;
          font-weight: 600;
        }
      `}</style>
    </div>
  )
}

export default AccountTopicsTab
